import i18next, { type i18n as I18nInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

export type Resources = Record<string, Record<string, object>>;

export interface CreateI18nOptions {
  /** `{ en: { common: {...}, products: {...} } }` */
  resources: Resources;
  defaultNS?: string;
  fallbackLng?: string;
  supportedLngs?: string[];
  /** Detect from the browser (querystring, localStorage, navigator). Default true. */
  detect?: boolean;
  debug?: boolean;
}

/**
 * Creates and initialises an i18next instance bound to react-i18next.
 * Keys are namespaced per feature: `t('products:list.title')`.
 * Missing keys are logged in development so agents notice untranslated strings.
 */
export function createI18n({
  resources,
  defaultNS = 'common',
  fallbackLng = 'en',
  supportedLngs = ['en'],
  detect = true,
  debug = false,
}: CreateI18nOptions): I18nInstance {
  const instance = i18next.createInstance();
  if (detect) {
    instance.use(LanguageDetector);
  }
  void instance.use(initReactI18next).init({
    resources,
    defaultNS,
    fallbackLng,
    supportedLngs,
    ns: Object.keys(resources[fallbackLng] ?? {}),
    interpolation: { escapeValue: false },
    returnNull: false,
    debug,
    detection: {
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: 'nucleus.lng',
      caches: ['localStorage'],
    },
    saveMissing: debug,
    ...(debug
      ? {
          missingKeyHandler: (_lngs: readonly string[], ns: string, key: string) => {
            console.warn(`[i18n] missing key ${ns}:${key}`);
          },
        }
      : {}),
  });
  return instance;
}
